import { useState, useEffect, useMemo } from 'react';
import {
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  useMap,
} from '@vis.gl/react-google-maps';
import {
  useChargePointsStore,
  type ChargePointsState,
} from '@/features/charge-points/store/charge-points-store';
import { useOperatorStore } from '@/features/operator/store/operator-store';
import { useFilterStore } from '@/features/filters/store/filter-store';
import { useLocaleStore } from '@/features/locale/store/locale-store';
import { ChargePointMarker } from './ChargePointMarker';
import { useClusterTuningStore } from '../store/cluster-tuning-store';
import { ClusterMarker } from './ClusterMarker';
import { OverlapListPopup } from './OverlapListPopup';
import {
  detectOverlappingMarkers,
  type MarkerWithOverlap,
} from '../utils/overlap-detection';
import type {
  ChargePointWrapper,
  ChargePointCluster,
} from '@/features/charge-points/types/charge-point.types';
import { getBrandConfig } from '@/features/operator/brand-config';
import {
  CLUSTER_STYLES,
  clusterTotal,
  gridFitMaxPx,
  mergeTouchingClusters,
  type ClusterStyle,
} from '../utils/cluster-layout';

interface ChargePointMarkersProps {
  chargePoints: ChargePointWrapper[];
  clusters: ChargePointCluster[];
  isLocked?: boolean;
}

/**
 * Component to render all charge point markers and clusters
 */
export function ChargePointMarkers({
  chargePoints,
  clusters: rawClusters,
  isLocked = false,
}: ChargePointMarkersProps) {
  const map = useMap();

  // ?cluster_style=square|disc|area|halo|outline (default square), ring = legacy
  const styleParam = new URLSearchParams(window.location.search).get(
    'cluster_style'
  );
  const clusterStyle: ClusterStyle | null =
    styleParam === 'ring'
      ? null
      : CLUSTER_STYLES.includes(styleParam as ClusterStyle)
        ? (styleParam as ClusterStyle)
        : 'square';

  const tuning = useClusterTuningStore();

  // Re-layout clusters when the zoom changes (sizes are in screen pixels)
  const [zoom, setZoom] = useState(map?.getZoom());
  useEffect(() => {
    if (!map) return;
    // The map instance and its projection can arrive after mount: read the
    // zoom now and again once the map is idle, not only on zoom_changed
    const sync = () => setZoom(map.getZoom());
    sync();
    const listeners = [
      map.addListener('zoom_changed', sync),
      map.addListener('idle', sync),
    ];
    return () => listeners.forEach((l) => l.remove());
  }, [map]);

  const { clusters, clusterMaxPx } = useMemo(() => {
    const projection = map?.getProjection();
    if (!clusterStyle || !projection || zoom === undefined)
      return { clusters: rawClusters, clusterMaxPx: undefined };
    const scale = 2 ** zoom;
    const toPx = (c: ChargePointCluster) => {
      const p = projection.fromLatLngToPoint(c.coordinate);
      return { x: (p?.x ?? 0) * scale, y: (p?.y ?? 0) * scale };
    };
    // Size bubbles to the spacing on screen so dense areas don't pile up
    // (~90% of the grid, or of the merge distance once merged that far)
    const maxPx = Math.min(
      tuning.maxPx,
      Math.max(gridFitMaxPx(rawClusters.map(toPx)), tuning.mergePx) * 0.9
    );
    return {
      clusters: mergeTouchingClusters(
        rawClusters,
        toPx,
        maxPx,
        tuning.mergePx,
        tuning.sizeExp
      ),
      clusterMaxPx: maxPx,
    };
  }, [
    rawClusters,
    map,
    zoom,
    clusterStyle,
    tuning.maxPx,
    tuning.mergePx,
    tuning.sizeExp,
  ]);
  const setTuningStats = useClusterTuningStore((s) => s.setStats);
  useEffect(
    () => setTuningStats({ raw: rawClusters.length, shown: clusters.length }),
    [rawClusters.length, clusters.length, setTuningStats]
  );
  const maxClusterCount = useMemo(
    () => Math.max(1, ...clusters.map(clusterTotal)),
    [clusters]
  );
  const selectChargePoint = useChargePointsStore(
    (s: ChargePointsState) => s.selectChargePoint
  );
  const openPinId = useChargePointsStore((s: ChargePointsState) => s.openPinId);
  const operator = useOperatorStore((s) => s.operator);
  const theme = useOperatorStore((s) => s.theme);
  const selectedOperatorIds = useFilterStore((s) => s.selectedOperatorIds);
  const markerStyle = useFilterStore((s) => s.markerStyle);
  const allOperators = useLocaleStore((s) => s.allOperators);

  // Check if operator parameter is in URL (not just persisted in store)
  const urlParams = new URLSearchParams(window.location.search);
  const hasOperatorUrlParam = urlParams.has('operator');

  // Brand override icon for the current operator (used when marker_style=operator)
  const operatorBrandConfig = operator
    ? getBrandConfig(operator.identifier)
    : null;
  const brandIconUrl =
    operatorBrandConfig &&
    (markerStyle === 'operator' || markerStyle === 'operator_neutral')
      ? operatorBrandConfig.pinIconUrl
      : theme?.portal_fav_icon_path;

  // Overlap detection state
  const [showOverlapPopup, setShowOverlapPopup] = useState<string | null>(null);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);
  const [overlapData, setOverlapData] = useState<{
    chargePoints: Map<string, MarkerWithOverlap>;
    clusters: Map<string, MarkerWithOverlap>;
    visibleChargePointIds: Set<string>;
    visibleClusterIds: Set<string>;
  } | null>(null);

  // Detect overlapping markers
  useEffect(() => {
    const data = detectOverlappingMarkers(chargePoints, clusters);
    setOverlapData(data);
  }, [chargePoints, clusters]);

  // Close overlap popup when clicking on map
  useEffect(() => {
    if (!map) return;

    const handleMapClick = () => {
      setShowOverlapPopup(null);
    };

    const listener = map.addListener('click', handleMapClick);

    return () => {
      // Remove specific listener instead of clearing all listeners
      if (listener) {
        google.maps.event.removeListener(listener);
      }
    };
  }, [map]);

  // Helper function to get operator theme data for a specific operator ID
  // When operator URL param exists: ONLY show icons for that specific operator
  // When no operator URL param: Show icons from cached operators (map_suggestions)
  const getOperatorTheme = (
    cpOperatorId?: number
  ): { color?: string; iconUrl?: string; operatorId: number } | null => {
    if (!cpOperatorId) {
      return null;
    }

    // Case 1: URL operator parameter is set (e.g., ?operator=norlys)
    // ONLY show icon if charge point belongs to that specific operator
    if (hasOperatorUrlParam && operator) {
      // Check if charge point belongs to the URL operator
      if (cpOperatorId === operator.id && theme) {
        return {
          color: theme.primary_color,
          iconUrl: brandIconUrl,
          operatorId: operator.id,
        };
      }
      // Operator param set but CP doesn't match - don't show any icon
      return null;
    }

    // Case 2: No operator URL param - check cached operators from map_suggestions
    const cachedOperator = allOperators.find((op) => {
      // Check if cpOperatorId matches either the suggestion ID or any of the operatorIds
      return op.id === cpOperatorId || op.operatorIds?.includes(cpOperatorId);
    });

    if (cachedOperator?.defaultTheme) {
      return {
        color: cachedOperator.defaultTheme.primary_color,
        iconUrl:
          cachedOperator.defaultTheme.portal_fav_icon_path ||
          cachedOperator.defaultTheme.logo_url,
        operatorId: cachedOperator.id,
      };
    }

    return null;
  };

  // Show operator icon logic:
  // IMPORTANT: operator= and operatorIds= are independent parameters
  // - operator=norlys → Theming + icons on matching pins
  // - operatorIds=602 → Filtering ONLY (shows icons from cached data)
  // - operator=norlys&operatorIds=602 → Both theming AND filtering (shows icons)
  // - No operator param → Shows icons from cached map_suggestions data
  const hasOperatorFilter = selectedOperatorIds.length > 0;
  const isSingleOperatorFilter = selectedOperatorIds.length === 1;
  const filterMatchesUrlOperator =
    operator && selectedOperatorIds.includes(operator.id);
  const showOperatorIconOnAllPins = !!(
    operator &&
    hasOperatorFilter &&
    isSingleOperatorFilter &&
    filterMatchesUrlOperator
  );

  // Handle charge point click - check for overlaps first
  const handleChargePointClick = (cp: ChargePointWrapper) => {
    if (isLocked) return; // Disable clicks when locked

    // Check if there are overlapping markers
    const overlapInfo = overlapData?.chargePoints.get(cp.id);
    if (overlapInfo && overlapInfo.overlapCount > 1) {
      // Toggle popup if clicking same marker
      if (showOverlapPopup === cp.id) {
        setShowOverlapPopup(null);
        return;
      }

      // Set overlap group in store for sidebar navigation
      const setOverlapGroup = useChargePointsStore.getState().setOverlapGroup;
      setOverlapGroup(overlapInfo.overlapGroup);

      // Close any other popup and show this one
      setShowOverlapPopup(cp.id);
    } else {
      // No overlaps, open directly (and close any open popup)
      setShowOverlapPopup(null);
      selectChargePoint(cp);
    }
  };

  // Handle cluster click - check for overlaps first, then zoom
  const handleClusterClick = (cluster: ChargePointCluster) => {
    if (isLocked) return; // Disable clicks when locked

    // Check if there are overlapping markers
    const overlapInfo = overlapData?.clusters.get(cluster.id);
    if (overlapInfo && overlapInfo.overlapCount > 1) {
      // Toggle popup if clicking same marker
      if (showOverlapPopup === cluster.id) {
        setShowOverlapPopup(null);
        return;
      }

      // Set overlap group in store for sidebar navigation
      const setOverlapGroup = useChargePointsStore.getState().setOverlapGroup;
      setOverlapGroup(overlapInfo.overlapGroup);

      // Close any other popup and show this one
      setShowOverlapPopup(cluster.id);
    } else {
      // No overlaps, zoom in (and close any open popup)
      setShowOverlapPopup(null);
      if (map) {
        const currentZoom = map.getZoom() || 10;
        map.panTo(cluster.coordinate);
        map.setZoom(Math.min(20, currentZoom + 1));
      }
    }
  };

  // Handle selecting an item from overlap popup
  const handleOverlapItemSelect = (
    item: ChargePointWrapper | ChargePointCluster
  ) => {
    // Clear overlap group when specific item is selected
    const clearOverlapGroup = useChargePointsStore.getState().setOverlapGroup;
    clearOverlapGroup([]);

    if ('count' in item) {
      // It's a cluster - zoom in
      if (map) {
        const currentZoom = map.getZoom() || 10;
        map.panTo(item.coordinate);
        map.setZoom(Math.min(20, currentZoom + 1));
      }
    } else {
      // It's a charge point - open details
      selectChargePoint(item);
    }
  };

  return (
    <>
      {/* Single charge points */}
      {chargePoints
        .filter((cp) => overlapData?.visibleChargePointIds.has(cp.id) ?? true)
        .map((cp) => {
          const overlapInfo = overlapData?.chargePoints.get(cp.id);
          const isShowingPopup = showOverlapPopup === cp.id;
          const isHovered = hoveredMarkerId === cp.id;
          // A stack is "open" when any site/charger in it is selected
          const isOpen =
            openPinId === cp.id ||
            !!overlapInfo?.overlapGroup.some((item) => item.id === openPinId);

          // Get operator theme for this charge point
          const cpOperatorId =
            cp.type === 'chargePoint'
              ? cp.chargePoint?.operator_id
              : cp.chargePointGroup?.operator_id;
          const operatorTheme = getOperatorTheme(cpOperatorId);

          return (
            <AdvancedMarker
              key={cp.id}
              position={cp.coordinate}
              onClick={() => handleChargePointClick(cp)}
              // Selected/hovered pins sit above their neighbours
              zIndex={
                isShowingPopup || isHovered ? 1000 : isOpen ? 999 : undefined
              }
            >
              <div
                className="relative"
                onMouseEnter={() => {
                  setHoveredMarkerId(cp.id);
                }}
                onMouseLeave={() => {
                  setHoveredMarkerId(null);
                }}
              >
                <ChargePointMarker
                  chargePoint={cp}
                  isOpen={isOpen}
                  showOperatorIcon={!!operatorTheme}
                  highlightColor={operatorTheme?.color || theme?.primary_color}
                  highlightIconUrl={
                    operatorTheme?.iconUrl || theme?.portal_fav_icon_path
                  }
                  overlapCount={overlapInfo?.overlapCount}
                  overlapGroup={overlapInfo?.overlapGroup}
                />
                {isShowingPopup && overlapInfo && (
                  <OverlapListPopup
                    items={overlapInfo.overlapGroup}
                    onSelectItem={handleOverlapItemSelect}
                    onClose={() => setShowOverlapPopup(null)}
                  />
                )}
              </div>
            </AdvancedMarker>
          );
        })}

      {/* Clusters */}
      {clusters
        .filter(
          (cluster) => overlapData?.visibleClusterIds.has(cluster.id) ?? true
        )
        .map((cluster) => {
          const overlapInfo = overlapData?.clusters.get(cluster.id);
          const isShowingPopup = showOverlapPopup === cluster.id;
          const isHovered = hoveredMarkerId === cluster.id;

          return (
            <AdvancedMarker
              key={cluster.id}
              position={cluster.coordinate}
              // An area bubble is centred on its area, not hung above it
              anchorPoint={
                clusterStyle ? AdvancedMarkerAnchorPoint.CENTER : undefined
              }
              onClick={() => handleClusterClick(cluster)}
              // Smaller clusters above bigger ones so overlaps never hide them
              zIndex={
                isShowingPopup || isHovered
                  ? 1000
                  : maxClusterCount - clusterTotal(cluster)
              }
            >
              <div
                className="relative"
                onMouseEnter={() => {
                  setHoveredMarkerId(cluster.id);
                }}
                onMouseLeave={() => {
                  setHoveredMarkerId(null);
                }}
              >
                <ClusterMarker
                  cluster={cluster}
                  showOperatorIcon={showOperatorIconOnAllPins}
                  highlightColor={theme?.primary_color}
                  highlightIconUrl={brandIconUrl}
                  overlapCount={overlapInfo?.overlapCount}
                  overlapGroup={overlapInfo?.overlapGroup}
                  maxCount={maxClusterCount}
                  maxPx={clusterMaxPx}
                  clusterStyle={clusterStyle}
                />
                {isShowingPopup && overlapInfo && (
                  <OverlapListPopup
                    items={overlapInfo.overlapGroup}
                    onSelectItem={handleOverlapItemSelect}
                    onClose={() => setShowOverlapPopup(null)}
                  />
                )}
              </div>
            </AdvancedMarker>
          );
        })}
    </>
  );
}
